import { DatePipe } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  InfoBannerComponent,
  SegmentedControlComponent,
  SegmentedControlItem,
} from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { PlatformSettings } from '../../../../models/platform.models';
import { PlatformPauseDuration } from '../../../../enums/platform-pause-duration.enum';
import { PlatformRecommendationMode } from '../../../../enums/platform-recommendation-mode.enum';
import { PlatformSettingsService } from '../../../../services/platform-settings.service';

@Component({
  selector: 'app-platform-settings-visibility',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    TranslocoPipe,
    NgSelectModule,
    InfoBannerComponent,
    SegmentedControlComponent,
  ],
  templateUrl: './platform-settings-visibility.component.html',
  styleUrl: './platform-settings-visibility.component.scss',
})
export class PlatformSettingsVisibilityComponent {
  private readonly settings = inject(PlatformSettingsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = input.required<FormGroup>();
  readonly saved = input.required<PlatformSettings>();
  readonly formTick = signal(0);

  readonly PlatformPauseDuration = PlatformPauseDuration;
  readonly pauseDurations = Object.values(PlatformPauseDuration);
  readonly modeItems: ReadonlyArray<SegmentedControlItem<PlatformRecommendationMode>> = [
    {
      label: 'jobboard.platform.settings.visibility.receive',
      value: PlatformRecommendationMode.RECEIVE,
    },
    {
      label: 'jobboard.platform.settings.visibility.pause',
      value: PlatformRecommendationMode.PAUSE,
    },
  ];

  constructor() {
    afterNextRender(() => {
      this.form()
        .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.formTick.update((tick) => tick + 1);
        });
    });
  }

  /**
   * Current recommendation mode from the form.
   * @returns Selected mode
   */
  selectedMode(): PlatformRecommendationMode {
    this.formTick();
    return this.form().controls['recommendationMode']
      .value as PlatformRecommendationMode;
  }

  /**
   * Whether job recommendations are paused.
   * @returns True when pause is selected
   */
  paused(): boolean {
    return this.selectedMode() === PlatformRecommendationMode.PAUSE;
  }

  /**
   * Pause end date to display, or null for an open-ended pause.
   * @returns ISO date or null
   */
  pausedUntil(): string | null {
    this.formTick();
    const duration = this.form().controls['pauseDuration'].value as PlatformPauseDuration;
    if (!this.paused() || duration === PlatformPauseDuration.UNTIL_ENABLED) {
      return null;
    }
    const saved = this.saved();
    if (
      saved.recommendationMode === PlatformRecommendationMode.PAUSE &&
      saved.pauseDuration === duration &&
      saved.pausedUntil
    ) {
      return saved.pausedUntil;
    }
    return this.settings.computePausedUntil(duration);
  }

  /**
   * Returns the translation key for a pause duration option.
   * @param duration - Pause duration
   * @returns Translation key
   */
  durationKey(duration: PlatformPauseDuration): string {
    return `jobboard.platform.settings.visibility.durations.${duration}`;
  }

  /**
   * Applies a recommendation-mode change and defaults a new pause to 6 months.
   * @param mode - Selected recommendation mode
   */
  onModeChange(mode: PlatformRecommendationMode): void {
    const control = this.form().controls['recommendationMode'];
    const previous = control.value as PlatformRecommendationMode;
    control.setValue(mode);
    control.markAsDirty();
    if (
      mode === PlatformRecommendationMode.PAUSE &&
      previous !== PlatformRecommendationMode.PAUSE
    ) {
      const duration = this.form().controls['pauseDuration'];
      duration.setValue(PlatformPauseDuration.SIX_MONTHS);
      duration.markAsDirty();
    }
    this.formTick.update((tick) => tick + 1);
  }

  /**
   * Refreshes derived pause copy after the duration changes.
   */
  onDurationChange(): void {
    this.formTick.update((tick) => tick + 1);
  }
}
